import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dimmer, Loader, Message } from 'semantic-ui-react';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { orderBy } from '@progress/kendo-data-query';
import { useComponentManager } from '../../providers/ComponentManagerProvider';
import { ManageFunctionsModal } from './ManageFunctionsModal';

// Blank cell instead of the literal "null".
const blank = value => (value == null || value === '' ? '' : value);

const PAGE_SIZE = 10;

const toRow = f => ({
    id: f.id,
    name: blank(f.name),
    description: blank(f.description),
    _raw: f,
});

export const FunctionsTab = ({ componentId, onCountChange }) => {
    const { getComponentFunctions, setComponentFunctions, getFunctions } = useComponentManager();

    const [functions, setFunctions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const [sort, setSort] = useState([{ field: 'name', dir: 'asc' }]);
    const [page, setPage] = useState(0);

    const [manageOpen, setManageOpen] = useState(false);

    // Guards against out-of-order loads when the component switches mid-flight.
    const requestToken = useRef(0);

    // Ref so an inline onCountChange prop can't churn `load` and loop the reload effect.
    const onCountChangeRef = useRef(onCountChange);
    onCountChangeRef.current = onCountChange;
    const reportCount = n => {
        if (onCountChangeRef.current) onCountChangeRef.current(n);
    };

    const load = useCallback(() => {
        if (!componentId) return Promise.resolve();
        const token = (requestToken.current += 1);
        setLoading(true);
        setError(false);
        return getComponentFunctions(componentId)
            .then(res => {
                if (token !== requestToken.current) return;
                const data = res?.data ?? [];
                setFunctions(data);
                setLoading(false);
                reportCount(data.length);
            })
            .catch(() => {
                if (token !== requestToken.current) return;
                setError(true);
                setLoading(false);
                setFunctions([]);
                reportCount(0);
            });
    }, [componentId, getComponentFunctions]);
    
    useEffect(() => {
        setPage(0);
        setSort([{ field: 'name', dir: 'asc' }]);
        load();
    }, [componentId, load]);

    const rows = functions.map(toRow);
    const sorted = orderBy(rows, sort);
    const paged = sorted.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

    if (error) {
        return (
            <Message negative data-testid="functions-error">
                <Message.Header>Failed to load functions</Message.Header>
                <Button basic size="small" onClick={load} style={{ marginTop: '0.5rem' }}>
                    Retry
                </Button>
            </Message>
        );
    }

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '0.75rem',
                }}
            >
                <Button
                    primary
                    onClick={() => setManageOpen(true)}
                    data-testid="manage-functions-button"
                >
                    Manage functions
                </Button>
            </div>

            <div style={{ position: 'relative' }}>
                <Dimmer active={loading} inverted>
                    <Loader>Loading...</Loader>
                </Dimmer>
                {!loading && rows.length === 0 ? (
                    <div
                        style={{ textAlign: 'center', padding: '2rem', color: '#8c979c' }}
                        data-testid="functions-empty"
                    >
                        <p>No functions assigned to this component.</p>
                    </div>
                ) : (
                    <Grid
                        data={paged}
                        sortable={true}
                        sort={sort}
                        onSortChange={e => {
                            setSort(e.sort);
                            setPage(0);
                        }}
                        pageable={{ pageSizes: false }}
                        skip={page * PAGE_SIZE}
                        take={PAGE_SIZE}
                        total={rows.length}
                        onPageChange={e => setPage(e.page.skip / e.page.take)}
                    >
                        <Column field="id" title="ID" width="100px" />
                        <Column field="name" title="Name" />
                        <Column field="description" title="Description" />
                    </Grid>
                )}
            </div>

            <ManageFunctionsModal
                open={manageOpen}
                setOpen={setManageOpen}
                componentId={componentId}
                currentFunctions={functions}
                setComponentFunctions={setComponentFunctions}
                getFunctions={getFunctions}
                onSaved={load}
            />
        </div>
    );
};
